import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { type Identifier, useDataProvider, useListContext } from "ra-core";

import type { CrmDataProvider } from "../providers/supabase/dataProvider";
import {
  type SummaryExcerpt,
  type TranscriptExcerpt,
  transcriptQuery,
} from "../providers/commons/transcriptSearch";
import { chunk } from "./excerptParts";
import {
  type MeetingTextMatch,
  pickMeetingTextMatch,
} from "./meetingTextMatch";

// Excerpts are parsed out of whole transcripts, so they are fetched a page at a
// time for the rows actually loaded, never for all 200 matches. Summary
// excerpts use the same pages.
const EXCERPT_PAGE = 25;

type TranscriptMatches = {
  /** Present when the meeting matched inside its transcript or a summary. */
  match: (id: Identifier) => MeetingTextMatch | undefined;
  capped: boolean;
};

const TranscriptMatchesContext = createContext<TranscriptMatches>({
  match: () => undefined,
  capped: false,
});

export const useTranscriptMatch = (id: Identifier | undefined) => {
  const { match } = useContext(TranscriptMatchesContext);
  return id == null ? undefined : match(id);
};

export const useTranscriptSearchCapped = () =>
  useContext(TranscriptMatchesContext).capped;

const idSet = (ids: Identifier[] | undefined) =>
  new Set((ids ?? []).map(String));

/**
 * Reads the list's search and loaded rows, and makes each row's transcript or
 * summary match available to its cells. The id query is the same memoised call
 * the data provider made for the list itself, so it costs no second request.
 */
export const TranscriptMatchesProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { filterValues, data } = useListContext();
  const query = transcriptQuery(filterValues?.q);

  const { data: found } = useQuery({
    queryKey: ["zoom_meetings", "searchMeetingText", query],
    queryFn: () => dataProvider.searchMeetingText(query as string),
    enabled: !!query,
    staleTime: 30_000,
  });

  const inTranscript = useMemo(() => idSet(found?.transcriptIds), [found]);
  const inSummary = useMemo(() => idSet(found?.summaryIds), [found]);
  const loaded = (data ?? []).map((row) => row.id as Identifier);
  const transcriptPages = chunk(
    loaded.filter((id) => inTranscript.has(String(id))),
    EXCERPT_PAGE,
  );
  const summaryPages = chunk(
    loaded.filter((id) => inSummary.has(String(id))),
    EXCERPT_PAGE,
  );

  const transcriptQueries = useQueries({
    queries: transcriptPages.map((ids) => ({
      queryKey: ["zoom_meetings", "transcriptExcerpts", query, ids],
      queryFn: () => dataProvider.transcriptExcerpts(query as string, ids),
      enabled: !!query,
      staleTime: 30_000,
    })),
  });
  const summaryQueries = useQueries({
    queries: summaryPages.map((ids) => ({
      queryKey: ["zoom_meetings", "summaryExcerpts", query, ids],
      queryFn: () => dataProvider.summaryExcerpts(query as string, ids),
      enabled: !!query,
      staleTime: 30_000,
    })),
  });

  const transcripts = new Map<string, TranscriptExcerpt>();
  for (const q of transcriptQueries) {
    for (const row of q.data ?? [])
      transcripts.set(String(row.meeting_id), row);
  }
  const summaries = new Map<string, SummaryExcerpt>();
  for (const q of summaryQueries) {
    for (const row of q.data ?? []) summaries.set(String(row.meeting_id), row);
  }

  const value: TranscriptMatches = {
    match: (id) =>
      pickMeetingTextMatch({
        inTranscript: inTranscript.has(String(id)),
        inSummary: inSummary.has(String(id)),
        transcript: transcripts.get(String(id)),
        summary: summaries.get(String(id)),
      }),
    capped: !!query && !!found?.capped,
  };

  return (
    <TranscriptMatchesContext.Provider value={value}>
      {children}
    </TranscriptMatchesContext.Provider>
  );
};
